import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  dispararBloco1,
  dispararBloco2,
  dispararBloco3,
  dispararEventosBackfill,
  dispararEventosIncremental,
  FalhaRelatorioItem,
  fetchJobDetalhe,
  fetchJobs,
  fetchOverview,
  fetchResumoEventos,
  Overview,
  RecoverOrigem,
  refreshTodasViews,
  reprocessarOrigem,
  uploadEasyAssist,
  uploadRecorrentes,
} from '../../../api/bancoSync/bancoSyncApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { notify } from '../../../shared/feedback/notify';
import BlocoCard, { Linha } from '../../../components/sync-painel/BlocoCard';
import HistoricoSection from '../../../components/sync-painel/HistoricoSection';
import JobDetalheModal from '../../../components/sync-painel/JobDetalheModal';
import ModalSync from '../../../components/sync-painel/ModalSync';
import PendenciaDetalheModal from '../components/PendenciaDetalheModal';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import TotaisSection from '../components/TotaisSection';
import { formatarDataHoraCurta, ROTULO_BLOCO } from '../../../components/sync-painel/statusBadge';

// Esqueleto de página e cabeçalho compartilhados (`ConteDo`, `recepcao-conte-do`,
// `recepcao-header-row`, `recepcao-title`) + a paginação do histórico. O import é
// explícito de propósito: contar com o CSS que outra tela carrega é acoplamento
// invisível (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';
// Vocabulário das telas de configuração: cards, tabelas, botões, hints e avisos.
import '../../admin-sistema/pages/css/adminSistema.css';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/aviso-de-tela.css';

/** Rotulos longos dos blocos, para o titulo do modal de detalhe. */
const TITULO_BLOCO: Record<string, string> = {
  bloco1: 'Bloco 1 — Referências SGA',
  bloco2: 'Bloco 2 — Boletos SGA',
  bloco3: 'Bloco 3 — Financeiro Nasajon',
  eventos: 'Eventos SGA — sinistros',
  recover: 'Reprocessamento',
};

/** Opcoes do filtro de tipo no historico desta tela. */
const OPCOES_BLOCO = [
  { valor: 'bloco1', rotulo: 'Bloco 1 — Referências SGA' },
  { valor: 'bloco2', rotulo: 'Bloco 2 — Boletos SGA' },
  { valor: 'bloco3', rotulo: 'Bloco 3 — Financeiro Nasajon' },
  { valor: 'eventos', rotulo: 'Eventos SGA (sinistros)' },
  { valor: 'recover', rotulo: 'Reprocessamento' },
];

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: '1. O que esta tela controla',
    conteudo: (
      <p>
        A base de dados que alimenta o módulo <strong>Contac</strong> é mantida por um serviço separado, que
        consolida quatro origens: o SGA/Hinova (associados, veículos e boletos das bases BH e RJ), a Nasajon
        (títulos, lançamentos e rateios), e duas planilhas enviadas manualmente (EasyAssist e Recorrentes).
        Esta tela dispara e acompanha essas sincronizações — e mostra o que deu certo, o que ficou de fora e o
        que ainda está pendente de reprocessamento.
      </p>
    ),
  },
  {
    titulo: '2. A ordem dos blocos',
    conteudo: (
      <p>
        A sincronização completa segue sempre a ordem <strong>Bloco 1</strong> (referências do SGA: mapeamentos,
        associados e veículos) → <strong>Bloco 2</strong> (boletos do SGA no período informado) →{' '}
        <strong>Bloco 3</strong> (financeiro Nasajon). O Bloco 3 vem por último porque, ao terminar, ele já
        atualiza todas as views que os relatórios do Contac leem — um único refresh cobre tudo. Só um job do SGA
        roda por vez (Bloco 1 ou 2), e só um pipeline Nasajon por vez: se algo já estiver em execução, o sistema
        avisa e aponta para o job em andamento em vez de criar outro.
      </p>
    ),
  },
  {
    titulo: '3. Full ou incremental (Bloco 3)',
    conteudo: (
      <p>
        O modo <strong>Full</strong> é o acerto de contas: faz backup, zera as tabelas da Nasajon e varre tudo de
        novo — é o único modo que remove registros excluídos na origem. Demora horas e roda sozinho todo dia 5 às
        02h. O modo <strong>Incremental</strong> busca só o que mudou desde a data informada (com 3 dias de
        sobreposição automática) e leva uma fração do tempo — é o indicado para o dia a dia. Use como data o dia
        do último sync bem-sucedido.
      </p>
    ),
  },
  {
    titulo: '4. Falha parcial não é começar de novo',
    conteudo: (
      <p>
        Quando uma execução termina em <strong>Falha parcial</strong>, o sistema grava exatamente o que ficou de
        fora (janelas, páginas, etapas) na seção <strong>Pendências</strong> — e clicar na pendência mostra{' '}
        <strong>cada item com o motivo da falha</strong>. O motivo importa: erro de autenticação pede a renovação
        do token (o modal oferece o botão) antes de reprocessar; instabilidade passageira, basta reprocessar. O
        botão <strong>Reprocessar</strong> refaz somente o que falhou, roda em segundo plano e aparece no{' '}
        <strong>Histórico de execuções</strong> como "Reprocessamento", com as etapas acompanháveis como qualquer
        sincronização. O que persistir vira uma nova pendência (com o motivo atualizado) e pode ser reprocessado de
        novo. Quando o reprocessamento zera tudo que faltava, a execução original muda de{' '}
        <strong>Falha parcial</strong> para <strong>Recuperado</strong> — e um sync completo com sucesso também
        marca as pendências antigas como obsoletas.
      </p>
    ),
  },
  {
    titulo: '5. O que o Reprocessar refaz, e o que ele não toca',
    conteudo: (
      <p>
        Cada etapa é refeita do jeito que faz sentido para ela: as que percorrem a API em páginas{' '}
        <strong>retomam da página onde pararam</strong>, sem gastar de novo o que já tinha vindo; as que varrem uma
        tabela inteira (as classificações por placa, o refresh das views) <strong>rodam do início</strong>, porque
        não existe página de onde retomar — e refazer não duplica nada, já que toda gravação é por atualização. Se a
        etapa refeita alimenta outras, essas outras vêm junto, e o{' '}
        <strong>refresh das views é sempre o último passo</strong>: refrescar no meio congelaria nos relatórios o
        estado incompleto que o reprocessamento está justamente consertando.
      </p>
    ),
  },
  {
    titulo: '6. Duas etapas ficam fora do Reprocessar, de propósito',
    conteudo: (
      <p>
        O <strong>backup</strong> e a <strong>limpeza das tabelas</strong> que abrem a sincronização completa nunca
        são refeitos por um reprocessamento. O motivo é proteger o dado: a limpeza zera as tabelas para que a
        varredura completa as preencha de novo, então refazê-la num reprocessamento apagaria exatamente o que a
        execução anterior conseguiu trazer. Se uma dessas duas falhar, o reprocessamento diz isso na tela e o
        caminho é <strong>disparar o Bloco 3 novamente</strong>, do começo.
      </p>
    ),
  },
  {
    titulo: '6a. Eventos SGA — o que esse card traz',
    conteudo: (
      <>
        <p>
          Os <strong>eventos</strong> são os sinistros registrados no SGA: quando aconteceu, onde, com qual veículo,
          em que situação está e quanto foi orçado de reparo. Nada disso era guardado até agora — a Oficina consulta
          o protocolo na hora do cadastro e descarta o resto. É esse card que alimenta o{' '}
          <strong>Dashboard Oficina</strong>.
        </p>
        <p>
          O <strong>incremental</strong> é o que roda sozinho todo dia às 05h: traz os cadastros recentes e passa de
          novo nos meses que ainda têm evento em aberto. Essa segunda parte existe porque{' '}
          <strong>mudar a situação de um evento não muda a data de cadastro dele</strong> — sem revisitar o mês, um
          evento que passou de aberto para finalizado ficaria eternamente com a situação antiga. O{' '}
          <strong>backfill</strong> varre um período inteiro, mês a mês, e serve para popular a base pela primeira
          vez ou recuperar um intervalo antigo.
        </p>
        <p>
          Repetir um período <strong>não duplica nada</strong>: cada evento é reconhecido e atualizado. E a linha{' '}
          <strong>Gravado na base</strong> do card responde a pergunta que o status do job não responde — o status
          diz que a execução terminou, a contagem diz quanto ficou guardado.
        </p>
        <p>
          Só a base do <strong>Rio</strong> devolve eventos: a de BH não tem esse acesso liberado no SGA, e por
          decisão de negócio (28/08/2026) isso não será resolvido — a oficina da Atual é uma só, no Rio. Por isso a
          execução marca BH como &quot;sem acesso&quot; e ainda assim termina com sucesso: não é falha, e
          reprocessar não mudaria nada.
        </p>
      </>
    ),
  },
  {
    titulo: '7. O que roda sozinho',
    conteudo: (
      <p>
        O serviço mantém crons próprios: os mapeamentos do SGA todos os dias (11h e 23h), associados e veículos aos
        sábados (19h e 19h30), <strong>os eventos todos os dias às 05h</strong> e o Bloco 3 Full no dia 5 de cada mês
        às 02h. Por decisão de 21/08/2026, quem executa esses crons é o servidor em nuvem — não dispare um Full
        manual perto da janela do dia 5: se houver execução em andamento, o disparo é recusado com aviso.
      </p>
    ),
  },
  {
    titulo: '8. Aba Totais por competência',
    conteudo: (
      <p>
        A aba <strong>Totais por competência</strong> mostra os valores financeiros somados por mês, em dois
        quadros deliberadamente diferentes: <strong>Direto das tabelas</strong> é o bruto — tudo que está gravado
        no banco, com os boletos em três linhas e os lançamentos Nasajon em bruto e em situação 1 — e{' '}
        <strong>Das views</strong> é o que os dashboards do Contac leem, já com as regras contábeis aplicadas
        (só boleto pago, rateio de taxas, exclusões de classificação, corte a partir de 01/2025). Os dois quadros
        <strong> não batem por definição</strong>: a diferença entre eles é a régua contábil agindo, e serve para
        rastrear de onde vem cada número. O período é filtrado por competência (MM/AAAA) no botão Filtros.
      </p>
    ),
  },
  {
    titulo: '9. As três linhas de boletos, e por que elas divergem',
    conteudo: (
      <p>
        <strong>Emitidos</strong> soma o valor do boleto; <strong>pagos</strong> soma o valor pago de todo boleto
        que tem data de pagamento; e <strong>pagos por mês de referência</strong> soma o valor pago de todo boleto
        cujo valor recebido não é zero, distribuído pelo <strong>mês de referência</strong> que o SGA declara. A
        terceira é a métrica que o financeiro considera a principal, e ela existe porque as outras duas deixam
        dinheiro de fora: um boleto pode ter valor recebido e <strong>não</strong> ter data de pagamento
        registrada, e nesse caso ele não aparece na linha de pagos. Medido em 25/08/2026 na base inteira: a
        diferença entre as duas linhas de pagos é de <strong>R$ 281,35</strong>, toda concentrada em 09/2024. Nas
        demais competências os valores batem centavo a centavo — o que é o esperado, não sinal de erro.
      </p>
    ),
  },
  {
    titulo: '9a. Por que cada linha de boleto aparece também por base (RJ e BH)',
    conteudo: (
      <p>
        Abaixo de cada uma das três linhas vêm as mesmas contas separadas por <strong>base</strong>. RJ e BH são
        operações distintas na Hinova, com login próprio: um relatório tirado da interface do SGA é sempre de{' '}
        <strong>uma base só</strong>, enquanto a linha consolidada aqui soma as duas. Comparar o consolidado com um
        relatório do SGA, portanto, dá diferença sem que exista erro nenhum — foi o que aconteceu em 25/08/2026,
        quando os R$ 123 mil da BH pareceram uma divergência de sincronização. Para conferir contra o SGA, use a
        linha da base correspondente. A soma das bases sempre fecha com o consolidado; se aparecer uma linha{' '}
        <strong>SEM BASE</strong>, são boletos gravados sem a base preenchida, e aí sim vale investigar.
      </p>
    ),
  },
  {
    titulo: '10. Planilhas e views',
    conteudo: (
      <p>
        Os uploads de <strong>EasyAssist</strong> (custos de reboque) e <strong>Recorrentes</strong> (vínculo
        consultor-participante) só precisam acontecer quando há planilha nova, e podem ser reenviados sem medo —
        linhas repetidas são ignoradas. Atenção: se o upload acontecer <strong>depois</strong> do Bloco 3, as views
        precisam de um refresh manual (botão <strong>Atualizar views</strong>), senão os relatórios do Contac
        não enxergam o dado novo.
      </p>
    ),
  },
];

const hoje = (): string => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const dataBr = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const DATA_BR_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

/** 409 = já existe job rodando: aviso com o job apontado, nunca toast de erro. */
const tratarErroDisparo = (error: unknown, oQue: string) => {
  const resposta = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
  if (resposta?.status === 409) {
    notify.warning(resposta.data?.message ?? `Já existe uma sincronização em execução — aguarde terminar para disparar ${oQue}.`);
    return;
  }
  notify.errorFrom(error, `Não foi possível disparar ${oQue}. Tente novamente; se persistir, acione a TI.`);
};

const SincronizacaoBasePage: React.FC = () => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState<'SINCRONIZACAO' | 'TOTAIS'>('SINCRONIZACAO');
  const [jobDetalheId, setJobDetalheId] = useState<string | null>(null);
  const [pendenciaSelecionada, setPendenciaSelecionada] = useState<FalhaRelatorioItem | null>(null);
  const [confirmBloco1, setConfirmBloco1] = useState(false);
  const [modalBloco2, setModalBloco2] = useState(false);
  const [modalBloco3, setModalBloco3] = useState(false);
  const [modalEventos, setModalEventos] = useState(false);
  const [confirmViews, setConfirmViews] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: overview,
    error: overviewError,
    isError: overviewFalhou,
    isLoading,
  } = useQuery<Overview>({
    queryKey: ['banco-sync', 'overview'],
    queryFn: fetchOverview,
    // Polling: 10s com job rodando (a etapa atual muda), 60s em repouso.
    refetchInterval: (query) => {
      const blocos = query.state.data?.blocos;
      const rodando = blocos && Object.values(blocos).some((j) => j?.status === 'RUNNING');
      return rodando ? 10_000 : 60_000;
    },
  });

  useEffect(() => {
    if (overviewFalhou) {
      notify.errorFrom(overviewError, 'Não foi possível carregar o painel de sincronização. Recarregue a página; se persistir, acione a TI.', {
        toastId: 'banco-sync-overview',
      });
    }
  }, [overviewFalhou, overviewError]);

  // Estado do espelho de eventos: o status do job diz que ele RODOU, isto diz o que ficou
  // GRAVADO. Consulta própria porque o overview não a traz (e não deve: é contagem de
  // tabela, não estado de job).
  const {
    data: resumoEventos,
    isError: resumoEventosFalhou,
  } = useQuery({
    queryKey: ['banco-sync', 'eventos-resumo'],
    queryFn: fetchResumoEventos,
    // Sem retry: o caso esperado de falha aqui é estrutural (tabela ainda não criada no
    // banco), e insistir só atrasaria o aviso.
    retry: false,
  });

  useEffect(() => {
    if (resumoEventosFalhou) {
      // Texto FIXO, não `errorFrom`: aqui o que importa é a CONSEQUÊNCIA (o card não sabe
      // dizer quanta coisa está gravada), não o código HTTP — e o motivo mais provável,
      // enquanto a migration da at-09 não for aplicada, é a tabela não existir, o que
      // chega como 500 genérico. Regra da at-65.
      notify.warning(
        'Não foi possível ler quantos eventos estão gravados. Se o sync de eventos ainda não foi liberado no banco, isso é esperado; se já foi, acione a TI.',
        { toastId: 'banco-sync-eventos-resumo' },
      );
    }
  }, [resumoEventosFalhou]);

  const recarregar = () => {
    queryClient.invalidateQueries({ queryKey: ['banco-sync'] });
  };

  // -------------------------------------------------------------------------
  // Disparos
  // -------------------------------------------------------------------------

  const bloco1Mutation = useMutation({
    mutationFn: dispararBloco1,
    onSuccess: (r) => {
      notify.success(`Bloco 1 iniciado em segundo plano (job ${r.jobId.slice(0, 8)}...). Acompanhe o progresso neste painel.`);
      recarregar();
    },
    onError: (e) => tratarErroDisparo(e, 'o Bloco 1'),
  });

  const [b2Inicio, setB2Inicio] = useState(`01/01/${new Date().getFullYear()}`);
  const [b2Fim, setB2Fim] = useState(hoje());
  const bloco2Mutation = useMutation({
    mutationFn: dispararBloco2,
    onSuccess: (r) => {
      notify.success(`Bloco 2 iniciado em segundo plano (job ${r.jobId.slice(0, 8)}...). Acompanhe o progresso neste painel.`);
      recarregar();
    },
    onError: (e) => tratarErroDisparo(e, 'o Bloco 2'),
  });

  const [b3Modo, setB3Modo] = useState<'full' | 'incremental'>('incremental');
  const [b3Desde, setB3Desde] = useState('');
  const bloco3Mutation = useMutation({
    mutationFn: dispararBloco3,
    onSuccess: (r) => {
      notify.success(`Bloco 3 iniciado em segundo plano (job ${r.jobId.slice(0, 8)}...). Acompanhe o progresso neste painel.`);
      recarregar();
    },
    onError: (e) => tratarErroDisparo(e, 'o Bloco 3'),
  });

  // Eventos: um card, dois modos. O backfill varre janelas mensais desde 01/01/2025 (ou o
  // período informado); o incremental é o mesmo do cron das 05h.
  const [evtModo, setEvtModo] = useState<'incremental' | 'backfill'>('incremental');
  const [evtDesde, setEvtDesde] = useState('');
  const [evtAte, setEvtAte] = useState('');
  const [evtDias, setEvtDias] = useState('30');
  const eventosMutation = useMutation({
    mutationFn: (opts: { modo: 'incremental'; dias?: number } | { modo: 'backfill'; desde?: string; ate?: string }) =>
      opts.modo === 'incremental'
        ? dispararEventosIncremental(opts.dias)
        : dispararEventosBackfill({ desde: opts.desde, ate: opts.ate }),
    onSuccess: (r) => {
      notify.success(`Sync de eventos iniciado em segundo plano (job ${r.jobId.slice(0, 8)}...). Acompanhe o progresso neste painel.`);
      recarregar();
    },
    onError: (e) => tratarErroDisparo(e, 'o sync de eventos'),
  });

  // -------------------------------------------------------------------------
  // Pendências (recover por origem)
  // -------------------------------------------------------------------------

  const recoverMutation = useMutation({
    mutationFn: (item: FalhaRelatorioItem) => reprocessarOrigem(item.origem as RecoverOrigem, item.arquivo),
    onSuccess: () => {
      notify.success(
        'Reprocessamento iniciado em segundo plano — acompanhe no Histórico de execuções. O que persistir vira uma nova pendência.',
      );
      setPendenciaSelecionada(null);
      recarregar();
    },
    onError: (e) => tratarErroDisparo(e, 'o reprocessamento'),
  });

  // -------------------------------------------------------------------------
  // Uploads e views
  // -------------------------------------------------------------------------

  const easyMutation = useMutation({
    mutationFn: uploadEasyAssist,
    onSuccess: (r) => {
      notify.success(`Planilha EasyAssist importada: ${r.insertedCount} linha(s) nova(s), ${r.skippedCount} repetida(s) ignorada(s).`);
      notify.warning('Se o Bloco 3 já rodou hoje, atualize as views (botão na seção Views) para o dado novo aparecer no Contac.', {
        toastId: 'banco-sync-views-pos-upload',
      });
      recarregar();
    },
    onError: (e) => notify.errorFrom(e, 'Não foi possível importar a planilha EasyAssist. Confira o arquivo e tente novamente.'),
  });

  const recorrenteMutation = useMutation({
    mutationFn: uploadRecorrentes,
    onSuccess: (r) => {
      const falhas = Array.isArray(r.falhas) ? r.falhas.length : 0;
      if (falhas > 0) {
        notify.warning(`Planilha de Recorrentes importada com ressalvas: ${r.atualizados} atualizado(s) e ${falhas} linha(s) com falha.`);
      } else {
        notify.success(`Planilha de Recorrentes importada: ${r.atualizados} vínculo(s) atualizado(s).`);
      }
      recarregar();
    },
    onError: (e) => notify.errorFrom(e, 'Não foi possível importar a planilha de Recorrentes. Confira o arquivo e tente novamente.'),
  });

  const viewsMutation = useMutation({
    mutationFn: refreshTodasViews,
    onSuccess: (r) => {
      notify.success(`Views atualizadas (${r.views.length} view(s), ${Math.round(r.duration_ms / 1000)}s).`);
      recarregar();
    },
    onError: (e) => notify.errorFrom(e, 'Não foi possível atualizar as views. Tente novamente; se persistir, acione a TI.'),
  });

  const blocos = overview?.blocos;
  const algumRodando = useMemo(
    () => Boolean(blocos && Object.values(blocos).some((j) => j?.status === 'RUNNING')),
    [blocos],
  );
  // ⚠️ Eventos entra AQUI, junto de bloco1/bloco2: no backend os três dividem o mesmo lock
  // (TIPOS_SGA — disputam a API da Hinova e a pool do Prisma). Sem esta linha, os botões
  // dos Blocos 1 e 2 ficariam habilitados durante um sync de eventos e o clique morreria
  // num 409 que o usuário não tinha como prever.
  const sgaOcupado =
    blocos?.bloco1?.status === 'RUNNING' ||
    blocos?.bloco2?.status === 'RUNNING' ||
    blocos?.eventos?.status === 'RUNNING' ||
    // Idem para a varredura de boletos por vencimento (comissionamentos/at-01): mesmo lock, e
    // ela pode ser disparada de FORA deste painel (telas de Comissionamento), então é
    // perfeitamente possível abrir esta tela com ela já rodando.
    blocos?.boletosVencimento?.status === 'RUNNING';
  const disparoPendente =
    bloco1Mutation.isPending || bloco2Mutation.isPending || bloco3Mutation.isPending || eventosMutation.isPending;

  // O que o card mostra em "Gravado na base". ⚠️ Falha de consulta NUNCA vira "nenhum
  // evento": as duas coisas são indistinguíveis para quem lê, e a segunda é uma afirmação
  // de negócio que só pode ser feita quando a consulta respondeu (regra da at-60).
  const { resumoEventosTexto, resumoEventosTitulo } = useMemo(() => {
    if (resumoEventosFalhou) {
      return {
        resumoEventosTexto: 'não foi possível verificar',
        resumoEventosTitulo: 'A consulta ao espelho de eventos falhou — este número não pôde ser conferido.',
      };
    }
    if (!resumoEventos) {
      return { resumoEventosTexto: 'consultando...', resumoEventosTitulo: undefined };
    }
    const bases = resumoEventos.porBase.filter((b) => b.total > 0);
    if (bases.length === 0) {
      return {
        resumoEventosTexto: 'nenhum evento gravado ainda',
        resumoEventosTitulo: 'A consulta respondeu: o espelho está vazio. Dispare o backfill para populá-lo.',
      };
    }
    const partes = bases.map((b) => `${b.total.toLocaleString('pt-BR')} (${b.base})`);
    const ultimo = bases
      .map((b) => b.ultimoCadastro)
      .filter((d): d is string => Boolean(d))
      .sort()
      .pop();
    const texto = `${partes.join(' · ')}${ultimo ? ` · até ${formatarDataHoraCurta(ultimo)}` : ''}`;
    return { resumoEventosTexto: texto, resumoEventosTitulo: texto };
  }, [resumoEventos, resumoEventosFalhou]);

  const ultimoRefreshViews = overview?.viewsRefresh?.[0];
  const desdeSugerido = useMemo(() => {
    const b3 = blocos?.bloco3;
    return b3 && b3.status === 'SUCESSO' ? dataBr(b3.startedAt) : '';
  }, [blocos]);

  const abrirBloco3 = () => {
    setB3Modo('incremental');
    setB3Desde(desdeSugerido);
    setModalBloco3(true);
  };

  const colunasPendencias = useMemo<ColunaTabela<FalhaRelatorioItem>[]>(
    () => [
      { titulo: 'Origem', largura: 160, texto: (i) => i.origem },
      { titulo: 'O que ficou de fora (e por quê)', largura: 340, texto: (i) => i.resumo },
      { titulo: 'Gerada em', largura: 150, texto: (i) => formatarDataHoraCurta(i.criadoEm) },
      {
        titulo: 'Ação',
        largura: 150,
        texto: () => 'Reprocessar',
        render: (i) => (
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            disabled={recoverMutation.isPending}
            onClick={(e) => {
              // A linha abre o detalhe; o botão não pode abrir o modal junto.
              e.stopPropagation();
              recoverMutation.mutate(i);
            }}
          >
            {recoverMutation.isPending ? 'Reprocessando...' : 'Reprocessar'}
          </button>
        ),
      },
    ],
    [recoverMutation],
  );

  return (
    <div className="ConteDo recepcao-conte-do">
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Sincronização da Base
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
            ?
          </button>
        </div>
      </div>

      <p className="admin-subtitle" style={{ margin: '0 0 24px 0' }}>
        Base de dados que alimenta o Contac: SGA/Hinova, Nasajon e planilhas. Dispare, acompanhe e trate as
        sincronizações por aqui.
      </p>

      {/* Abas no padrão da Recepção (classes do Recepcao.css, já importado) */}
      <div className="recepcao-tabs">
        <button
          type="button"
          className={`recepcao-tab ${abaAtiva === 'SINCRONIZACAO' ? 'active' : ''}`}
          onClick={() => setAbaAtiva('SINCRONIZACAO')}
        >
          Sincronização
        </button>
        <button
          type="button"
          className={`recepcao-tab ${abaAtiva === 'TOTAIS' ? 'active' : ''}`}
          onClick={() => setAbaAtiva('TOTAIS')}
        >
          Totais por competência
        </button>
      </div>

      {abaAtiva === 'TOTAIS' && <TotaisSection />}

      {abaAtiva === 'SINCRONIZACAO' && overviewFalhou && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar o estado das sincronizações. As informações abaixo podem estar desatualizadas —
          recarregue a página; se persistir, acione a TI.
        </div>
      )}

      {abaAtiva === 'SINCRONIZACAO' && isLoading && (
        <div className="admin-vazio">Carregando o estado das sincronizações...</div>
      )}

      {abaAtiva === 'SINCRONIZACAO' && overview && (
        <>
          <div className="admin-card" style={{ marginBottom: 24, padding: '16px 24px' }}>
            <div className="admin-card-title" style={{ marginBottom: 8, fontSize: 15 }}>
              Como sincronizar tudo
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 10, rowGap: 6 }}>
              {[
                'Dispare o Bloco 1 e aguarde terminar',
                'Dispare o Bloco 2 com o período (só um job do SGA roda por vez)',
                'Envie as planilhas, SE houver arquivo novo',
                'Dispare o Bloco 3 — ao terminar, ele atualiza as views sozinho',
              ].map((passo, i, todos) => (
                <span key={passo} className="admin-texto-secundario" style={{ color: 'var(--tema-texto, #1A1C1B)' }}>
                  <strong style={{ color: '#EA5D0E' }}>{i + 1}.</strong> {passo}
                  {i < todos.length - 1 && <span style={{ color: '#CACACA', margin: '0 2px 0 10px' }}>›</span>}
                </span>
              ))}
            </div>
            <p className="admin-hint" style={{ marginTop: 8, marginBottom: 0 }}>
              Terminou em <strong>Falha parcial</strong>? Não repita o bloco: use o <strong>Reprocessar</strong> da
              seção Pendências — ele refaz somente o que falhou. Planilha enviada depois do Bloco 3 pede o botão{' '}
              <strong>Atualizar views</strong>.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 20,
              alignItems: 'stretch',
              marginBottom: 24,
            }}
          >
            <BlocoCard
              titulo="Bloco 1 — Referências SGA"
              descricao="Mapeamentos, associados e veículos das bases BH e RJ. É o primeiro passo de toda atualização completa."
              job={blocos?.bloco1 ?? null}
              onVerDetalhe={setJobDetalheId}
              acoes={
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={disparoPendente || sgaOcupado}
                  onClick={() => setConfirmBloco1(true)}
                >
                  Disparar Bloco 1 (confirma antes)
                </button>
              }
            />
            <BlocoCard
              titulo="Bloco 2 — Boletos SGA"
              descricao="Entradas financeiras (boletos) do período informado. Divide a fila com o Bloco 1: só um job do SGA por vez."
              job={blocos?.bloco2 ?? null}
              onVerDetalhe={setJobDetalheId}
              acoes={
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={disparoPendente || sgaOcupado}
                  onClick={() => setModalBloco2(true)}
                >
                  Disparar Bloco 2 (escolher período)
                </button>
              }
            />
            <BlocoCard
              titulo="Bloco 3 — Financeiro Nasajon"
              descricao="Títulos, lançamentos e rateios. Ao terminar, atualiza todas as views que o Contac lê. O Full roda sozinho todo dia 5 às 02h."
              job={blocos?.bloco3 ?? null}
              onVerDetalhe={setJobDetalheId}
              acoes={
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={disparoPendente || blocos?.bloco3?.status === 'RUNNING'}
                  onClick={abrirBloco3}
                >
                  Disparar Bloco 3 (escolher modo)
                </button>
              }
            />
            <BlocoCard
              titulo="Eventos SGA — sinistros"
              descricao="Os eventos (sinistros) das bases do SGA, que alimentam o Dashboard Oficina. Divide a fila com os Blocos 1 e 2: só um job do SGA por vez. O incremental roda sozinho todo dia às 05h."
              job={blocos?.eventos ?? null}
              onVerDetalhe={setJobDetalheId}
              extra={
                <Linha rotulo="Gravado na base" title={resumoEventosTitulo}>
                  {resumoEventosTexto}
                </Linha>
              }
              acoes={
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={disparoPendente || sgaOcupado}
                  onClick={() => setModalEventos(true)}
                >
                  Disparar Eventos (escolher modo)
                </button>
              }
            />
          </div>

          <div className="admin-card-title" style={{ marginBottom: 4 }}>
            Pendências de reprocessamento
          </div>
          <p className="admin-hint" style={{ marginTop: 0, marginBottom: 12 }}>
            O que ficou de fora das execuções com falha parcial. Clique numa linha para ver <strong>cada item e o
            motivo da falha</strong>; Reprocessar refaz somente o que falhou — nunca a sincronização inteira.
          </p>

          <div
            style={{
              width: '100%',
              paddingTop: 30,
              background: 'var(--tema-superficie, white)',
              borderRadius: 16,
              border: '1px var(--tema-linha-2, #CACACA) solid',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              marginBottom: 24,
            }}
          >
            <TabelaSistema
              colunas={colunasPendencias}
              itens={overview.falhasPendentes.itens}
              chave={(i) => i.id}
              vazio="Nenhuma pendência: tudo que foi sincronizado está completo."
              onClickLinha={setPendenciaSelecionada}
            />
          </div>

          {recoverMutation.isPending && (
            <p className="admin-hint" style={{ marginTop: -12, marginBottom: 20 }}>
              Reprocessamento em andamento — os de associados, veículos e boletos podem levar vários minutos; não
              feche esta tela.
            </p>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
              gap: 20,
              alignItems: 'stretch',
              marginBottom: 24,
            }}
          >
            <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
              <div className="admin-card-title" style={{ marginBottom: 8 }}>
                Planilhas (uploads manuais)
              </div>
              <p className="admin-hint" style={{ margin: '0 0 14px 0' }}>
                Só quando houver planilha nova. Linhas repetidas são ignoradas — reenviar o mesmo arquivo é seguro.
              </p>

              <div className="admin-input-group">
                <label className="admin-label" htmlFor="upload-easy">
                  EasyAssist (custos de reboque, CSV)
                </label>
                <input
                  id="upload-easy"
                  className="admin-input"
                  type="file"
                  accept=".csv"
                  disabled={easyMutation.isPending}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) easyMutation.mutate(f);
                    e.target.value = '';
                  }}
                />
              </div>

              <div className="admin-input-group">
                <label className="admin-label" htmlFor="upload-recorrentes">
                  Recorrentes (vínculo consultor-participante, CSV)
                </label>
                <input
                  id="upload-recorrentes"
                  className="admin-input"
                  type="file"
                  accept=".csv"
                  disabled={recorrenteMutation.isPending}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) recorrenteMutation.mutate(f);
                    e.target.value = '';
                  }}
                />
              </div>

              {(easyMutation.isPending || recorrenteMutation.isPending) && (
                <p className="admin-hint" style={{ marginBottom: 0 }}>
                  Importando a planilha...
                </p>
              )}
            </div>

            <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
              <div className="admin-card-title" style={{ marginBottom: 8 }}>
                Views (o que o Contac lê)
              </div>
              <p className="admin-hint" style={{ margin: '0 0 14px 0' }}>
                As views se atualizam sozinhas ao final do Bloco 3. O botão serve para uploads feitos depois do
                Bloco 3 — a atualização leva alguns minutos.
              </p>

              <div className="admin-linha-item" style={{ padding: '7px 0', gap: 12, justifyContent: 'flex-start' }}>
                <span className="admin-texto-secundario" style={{ width: 118, flexShrink: 0 }}>
                  Última atualização
                </span>
                <span className="admin-texto-secundario" style={{ color: 'var(--tema-texto, #1A1C1B)' }}>
                  {ultimoRefreshViews
                    ? `${formatarDataHoraCurta(ultimoRefreshViews.refreshed_at)} (${Math.round(Number(ultimoRefreshViews.duration_ms) / 1000)}s)`
                    : 'sem registro'}
                </span>
              </div>

              <button
                type="button"
                className="admin-btn admin-btn-primary"
                style={{ marginTop: 'auto', alignSelf: 'flex-start', paddingTop: 10 }}
                disabled={viewsMutation.isPending}
                onClick={() => setConfirmViews(true)}
              >
                {viewsMutation.isPending ? 'Atualizando views...' : 'Atualizar views (confirma antes)'}
              </button>
            </div>
          </div>

          <div className="admin-card" style={{ marginBottom: 24 }}>
            <div className="admin-card-title" style={{ marginBottom: 8 }}>
              O que roda sozinho
            </div>
            <p className="admin-hint" style={{ margin: '0 0 10px 0' }}>
              Crons do próprio serviço, em nuvem. Evite disparar um Full manual perto da janela do dia 5.
            </p>
            {overview.crons.map((c) => (
              <div
                className="admin-linha-item"
                key={c.quando + c.oque}
                style={{ padding: '7px 0', gap: 16, justifyContent: 'flex-start' }}
              >
                <span className="admin-texto-secundario" style={{ width: 220, flexShrink: 0 }}>
                  {c.quando}
                </span>
                <span className="admin-texto-secundario" style={{ color: 'var(--tema-texto, #1A1C1B)' }}>
                  {c.oque}
                </span>
              </div>
            ))}
          </div>

          <HistoricoSection
            onVerDetalhe={setJobDetalheId}
            algumRodando={algumRodando}
            fetchJobs={fetchJobs}
            queryKeyBase="banco-sync"
            rotulosTipo={ROTULO_BLOCO}
            opcoesTipo={OPCOES_BLOCO}
            tituloColunaTipo="Bloco"
          />
        </>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Sincronização da Base"
        secoes={helpSections}
      />

      {jobDetalheId && (
        <JobDetalheModal
          jobId={jobDetalheId}
          onClose={() => setJobDetalheId(null)}
          fetchDetalhe={fetchJobDetalhe}
          queryKeyBase="banco-sync"
          titulosPorTipo={TITULO_BLOCO}
        />
      )}

      {pendenciaSelecionada && (
        <PendenciaDetalheModal
          item={pendenciaSelecionada}
          onClose={() => setPendenciaSelecionada(null)}
          onReprocessar={(i) => recoverMutation.mutate(i)}
          reprocessando={recoverMutation.isPending}
        />
      )}

      <ConfirmModal
        isOpen={confirmBloco1}
        title="Disparar Bloco 1"
        confirmLabel="Disparar"
        message={
          'O Bloco 1 sincroniza os mapeamentos, os associados e os veículos do SGA (bases BH e RJ), em segundo plano. ' +
          'A etapa de associados é a mais longa (30 a 90 minutos). Nada é apagado — os dados são inseridos ou atualizados.'
        }
        onCancel={() => setConfirmBloco1(false)}
        onConfirm={() => {
          setConfirmBloco1(false);
          bloco1Mutation.mutate();
        }}
      />

      <ConfirmModal
        isOpen={confirmViews}
        title="Atualizar as views agora"
        confirmLabel="Atualizar"
        message={
          'Reexecuta as 16 views que os relatórios do Contac leem, na ordem correta. Leva alguns minutos. ' +
          'Só é necessário quando um upload ou sync rodou depois do Bloco 3 — o Bloco 3 já faz isso sozinho ao terminar.'
        }
        onCancel={() => setConfirmViews(false)}
        onConfirm={() => {
          setConfirmViews(false);
          viewsMutation.mutate();
        }}
      />

      {modalBloco2 && (
        <ModalSync
          titulo="Disparar Bloco 2 — Boletos SGA"
          subtitulo="Informe o período de emissão dos boletos"
          onClose={() => setModalBloco2(false)}
          footer={
            <>
              <button type="button" className="filtro-btn-secondary" onClick={() => setModalBloco2(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="filtro-btn-primary"
                onClick={() => {
                  if (!DATA_BR_REGEX.test(b2Inicio) || !DATA_BR_REGEX.test(b2Fim)) {
                    notify.warning('Informe as duas datas no formato DD/MM/AAAA.');
                    return;
                  }
                  setModalBloco2(false);
                  bloco2Mutation.mutate({ data_emissao_inicial: b2Inicio, data_emissao_final: b2Fim });
                }}
              >
                Disparar
              </button>
            </>
          }
        >
          <div className="filtro-section">
            <h3 className="filtro-section-title">Período de emissão</h3>
            <p className="filtro-field-hint">
              Para a rotina mensal, o ano corrente é suficiente — boletos antigos raramente mudam, e períodos
              maiores só aumentam a duração.
            </p>
            <div className="filtro-grid-2">
              <div className="filtro-field">
                <label htmlFor="b2-inicio">Data inicial</label>
                <input
                  id="b2-inicio"
                  className="premium-text-input"
                  type="text"
                  value={b2Inicio}
                  placeholder="DD/MM/AAAA"
                  onChange={(e) => setB2Inicio(e.target.value)}
                />
              </div>
              <div className="filtro-field">
                <label htmlFor="b2-fim">Data final</label>
                <input
                  id="b2-fim"
                  className="premium-text-input"
                  type="text"
                  value={b2Fim}
                  placeholder="DD/MM/AAAA"
                  onChange={(e) => setB2Fim(e.target.value)}
                />
              </div>
            </div>
          </div>
        </ModalSync>
      )}

      {modalBloco3 && (
        <ModalSync
          titulo="Disparar Bloco 3 — Financeiro Nasajon"
          subtitulo="Escolha o modo de execução"
          onClose={() => setModalBloco3(false)}
          footer={
            <>
              <button type="button" className="filtro-btn-secondary" onClick={() => setModalBloco3(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className={b3Modo === 'full' ? 'admin-btn admin-btn-danger' : 'filtro-btn-primary'}
                onClick={() => {
                  if (b3Modo === 'incremental' && !DATA_BR_REGEX.test(b3Desde)) {
                    notify.warning('Informe a data de corte no formato DD/MM/AAAA.');
                    return;
                  }
                  setModalBloco3(false);
                  bloco3Mutation.mutate(b3Modo === 'full' ? { mode: 'full' } : { mode: 'incremental', desde: b3Desde });
                }}
              >
                {b3Modo === 'full' ? 'Disparar Full (zera e refaz)' : 'Disparar incremental'}
              </button>
            </>
          }
        >
          <div className="filtro-section">
            <h3 className="filtro-section-title">Modo</h3>

            <label className="admin-checkbox-label">
              <input type="radio" name="b3modo" checked={b3Modo === 'incremental'} onChange={() => setB3Modo('incremental')} />
              <span>
                <strong>Incremental</strong> — busca só o que mudou desde a data de corte (com 3 dias de
                sobreposição). Rápido; indicado para o dia a dia. Não remove registros excluídos na Nasajon.
              </span>
            </label>

            {b3Modo === 'incremental' && (
              <div className="filtro-field" style={{ marginTop: 10 }}>
                <label htmlFor="b3-desde">Buscar mudanças desde (data do último sync bem-sucedido)</label>
                <input
                  id="b3-desde"
                  className="premium-text-input"
                  type="text"
                  value={b3Desde}
                  placeholder="DD/MM/AAAA"
                  onChange={(e) => setB3Desde(e.target.value)}
                />
              </div>
            )}

            <label className="admin-checkbox-label" style={{ marginTop: 12 }}>
              <input type="radio" name="b3modo" checked={b3Modo === 'full'} onChange={() => setB3Modo('full')} />
              <span>
                <strong>Full (reconciliador)</strong> — faz backup, <strong>ZERA as 11 tabelas da Nasajon</strong> e
                varre tudo de novo. É o único modo que remove registros excluídos na origem. Demora horas; roda
                sozinho todo dia 5 às 02h — dispare manualmente só quando houver suspeita de divergência.
              </span>
            </label>

            {b3Modo === 'full' && (
              <div className="aviso-de-tela" style={{ marginTop: 12 }}>
                Durante a execução do Full, os dados da Nasajon ficam temporariamente incompletos para o Contac. Se a
                execução for interrompida no meio, será preciso disparar outro Full. Não dispare perto da janela
                automática do dia 5 às 02h.
              </div>
            )}
          </div>
        </ModalSync>
      )}

      {modalEventos && (
        <ModalSync
          titulo="Disparar Eventos SGA — sinistros"
          subtitulo="Escolha o modo de execução"
          onClose={() => setModalEventos(false)}
          footer={
            <>
              <button type="button" className="filtro-btn-secondary" onClick={() => setModalEventos(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="filtro-btn-primary"
                onClick={() => {
                  if (evtModo === 'backfill') {
                    // As duas datas são OPCIONAIS (sem elas o backend varre desde
                    // 01/01/2025), mas o que for preenchido precisa estar no formato —
                    // data torta viraria 400 depois do modal já ter fechado.
                    for (const [rotulo, valor] of [['inicial', evtDesde], ['final', evtAte]] as const) {
                      if (valor && !DATA_BR_REGEX.test(valor)) {
                        notify.warning(`Informe a data ${rotulo} no formato DD/MM/AAAA, ou deixe em branco.`);
                        return;
                      }
                    }
                    setModalEventos(false);
                    eventosMutation.mutate({ modo: 'backfill', desde: evtDesde || undefined, ate: evtAte || undefined });
                    return;
                  }
                  const dias = parseInt(evtDias, 10);
                  if (!Number.isFinite(dias) || dias < 1 || dias > 30) {
                    notify.warning('Informe um número de dias entre 1 e 30 (o limite é da API do SGA).');
                    return;
                  }
                  setModalEventos(false);
                  eventosMutation.mutate({ modo: 'incremental', dias });
                }}
              >
                {evtModo === 'backfill' ? 'Disparar backfill' : 'Disparar incremental'}
              </button>
            </>
          }
        >
          <div className="filtro-section">
            <h3 className="filtro-section-title">Modo</h3>

            <label className="admin-checkbox-label">
              <input
                type="radio"
                name="evtmodo"
                checked={evtModo === 'incremental'}
                onChange={() => setEvtModo('incremental')}
              />
              <span>
                <strong>Incremental</strong> — o mesmo que roda sozinho às 05h: os eventos cadastrados nos últimos
                dias, mais uma nova passagem nos meses que ainda têm evento em aberto. É essa segunda parte que
                percebe quando um evento antigo muda de situação.
              </span>
            </label>

            {evtModo === 'incremental' && (
              <div className="filtro-field" style={{ marginTop: 10 }}>
                <label htmlFor="evt-dias">Buscar cadastros dos últimos (dias)</label>
                <input
                  id="evt-dias"
                  className="premium-text-input"
                  type="number"
                  min={1}
                  max={30}
                  value={evtDias}
                  onChange={(e) => setEvtDias(e.target.value)}
                />
                <p className="admin-hint" style={{ marginTop: 6 }}>
                  Máximo de 30 dias — é o intervalo que a API do SGA aceita por consulta.
                </p>
              </div>
            )}

            <label className="admin-checkbox-label" style={{ marginTop: 12 }}>
              <input type="radio" name="evtmodo" checked={evtModo === 'backfill'} onChange={() => setEvtModo('backfill')} />
              <span>
                <strong>Backfill</strong> — varre um período inteiro, mês a mês. Use para popular a base pela
                primeira vez ou para recuperar um intervalo antigo. Nada é apagado: eventos já gravados são
                atualizados, e repetir o mesmo período não duplica nada.
              </span>
            </label>

            {evtModo === 'backfill' && (
              <>
                <div className="filtro-grid-2" style={{ marginTop: 10 }}>
                  <div className="filtro-field">
                    <label htmlFor="evt-desde">De (opcional)</label>
                    <input
                      id="evt-desde"
                      className="premium-text-input"
                      type="text"
                      value={evtDesde}
                      placeholder={resumoEventos?.periodoPadraoBackfill.desde ?? 'DD/MM/AAAA'}
                      onChange={(e) => setEvtDesde(e.target.value)}
                    />
                  </div>
                  <div className="filtro-field">
                    <label htmlFor="evt-ate">Até (opcional)</label>
                    <input
                      id="evt-ate"
                      className="premium-text-input"
                      type="text"
                      value={evtAte}
                      placeholder={resumoEventos?.periodoPadraoBackfill.ate ?? 'DD/MM/AAAA'}
                      onChange={(e) => setEvtAte(e.target.value)}
                    />
                  </div>
                </div>
                <p className="admin-hint" style={{ marginTop: 8 }}>
                  Deixando os dois campos em branco, o período padrão é
                  {resumoEventos
                    ? ` de ${resumoEventos.periodoPadraoBackfill.desde} até ${resumoEventos.periodoPadraoBackfill.ate}`
                    : ' o intervalo completo definido pelo servidor'}
                  . Quanto maior o período, mais tempo leva — a varredura é mês a mês.
                </p>
              </>
            )}

            <div className="aviso-de-tela" style={{ marginTop: 12 }}>
              Só a base do Rio retorna eventos. A base BH não tem esse acesso liberado no SGA e sai marcada como
              &quot;sem permissão&quot; no resultado — isso é o esperado, não é falha da execução.
            </div>
          </div>
        </ModalSync>
      )}
    </div>
  );
};

export default SincronizacaoBasePage;
